import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function PublicPDFReceipt() {
  const { id: token } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      fetchPDFUrl();
    }
  }, [token]);

  const fetchPDFUrl = async () => {
    console.log('🔍 Fetching PDF for token:', token);
    
    try {
      if (!token) {
        setError('No receipt token provided');
        setLoading(false);
        return;
      }

      if (!supabase) {
        setError('Service temporarily unavailable');
        setLoading(false);
        return;
      }

      // Normalize token (lowercase for hex tokens)
      const normalizedToken = token.trim().toLowerCase();
      
      // Look up the token in the database
      let { data: tokenData, error: tokenError } = await supabase
        .from('public_receipt_tokens')
        .select('payment_id, business_id, booking_id, expires_at')
        .eq('token', normalizedToken)
        .single();

      if (tokenError || !tokenData) {
        // Try case-sensitive lookup as fallback
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('public_receipt_tokens')
          .select('payment_id, business_id, booking_id, expires_at')
          .eq('token', token)
          .single();
          
        if (fallbackError || !fallbackData) {
          setError('Receipt not found or has expired');
          setLoading(false);
          return;
        }
        
        tokenData = fallbackData;
      }

      // Check if token is expired
      if (new Date(tokenData.expires_at) < new Date()) {
        setError('This receipt link has expired');
        setLoading(false);
        return;
      }

      // Get the PDF URL from the payment record
      if (tokenData.payment_id && tokenData.payment_id !== 'pending') {
        const { data: paymentData, error: paymentError } = await supabase
          .from('payments')
          .select('pdf_url')
          .eq('id', tokenData.payment_id)
          .single();

        if (paymentError || !paymentData) {
          setError('Payment record not found');
          setLoading(false);
          return;
        }

        if (paymentData.pdf_url) {
          console.log('✅ Found PDF URL:', paymentData.pdf_url);
          setPdfUrl(paymentData.pdf_url);
          
          // Automatically redirect to PDF
          window.location.href = paymentData.pdf_url;
          return;
        } else {
          setError('PDF receipt not available for this payment');
          setLoading(false);
          return;
        }
      } else {
        // For pending payments, we might not have a PDF yet
        setError('PDF receipt not yet available for pending payments');
        setLoading(false);
        return;
      }
      
    } catch (error) {
      console.error('Error fetching PDF URL:', error);
      setError('An error occurred while loading the receipt');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading receipt...</p>
        </div>
      </div>
    );
  }

  // Show error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="max-w-md w-full bg-white p-6 rounded-lg shadow-md text-center">
          <div className="text-red-600 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-semibold mt-2">Receipt Not Available</h2>
          </div>
          <p className="text-gray-700">{error}</p>
          {pdfUrl && (
            <div className="mt-4">
              <a 
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Open PDF Receipt
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  // This should rarely be reached since we redirect to PDF
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="max-w-md w-full bg-white p-6 rounded-lg shadow-md text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Receipt Found</h2>
        <p className="text-gray-700 mb-4">Your PDF receipt should open automatically.</p>
        {pdfUrl && (
          <a 
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Open PDF Receipt
          </a>
        )}
      </div>
    </div>
  );
} 